import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth, nextPathFor, isStaffRole } from '../../context/AuthContext';

const TEXT = {
  ar: {
    subtitle: 'تسجيل الدخول',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    submit: 'دخول',
    submitting: 'جاري الدخول...',
    register: 'ليس لديك حساب؟ سجّل هنا',
    noAccess: 'حسابك لا يملك صلاحية الدخول بعد. يرجى التواصل مع المسؤول لتفعيل حسابك.',
  },
  en: {
    subtitle: 'Sign in',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in...',
    register: 'Need an account? Register here',
    noAccess: 'Your account does not have access yet. Please ask an administrator to activate it.',
  },
};

// The single sign-in page. Admins and techs check in (attendance) first and
// then land on their own dashboard.
const Login = () => {
  const { lang = 'ar' } = useParams();
  const t = TEXT[lang] || TEXT.ar;
  const navigate = useNavigate();
  const from = useLocation().state?.from;
  const { currentUser, userRole, login, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Already signed in (e.g. opened /login again): go straight on
  useEffect(() => {
    if (currentUser && isStaffRole(userRole) && !submitting) {
      navigate(nextPathFor(userRole, lang, from), { replace: true });
    }
  }, [currentUser, userRole, submitting, navigate, lang, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const user = await login(email, password);
      if (!isStaffRole(user.role)) {
        await logout();
        setError(t.noAccess);
        setSubmitting(false);
        return;
      }
      navigate(`/${lang}/attendance`, { replace: true, state: { fromLogin: true, next: from } });
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold">البروفيسور للفحص الفني للمركبات</h1>
          <h2 className="text-xl" dir="ltr">ALPROFESSOR VEHICLES TECHNICAL TESTING</h2>
          <p className="mt-2 text-gray-600">{t.subtitle}</p>
        </div>

        {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">{t.email}</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">{t.password}</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              dir="ltr"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-2 px-4 rounded w-full"
          >
            {submitting ? t.submitting : t.submit}
          </button>
          <div className="text-center">
            <Link to={`/${lang}/register`} className="text-blue-600 hover:text-blue-800 text-sm">{t.register}</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
